import { pushWithRegion } from 'actions/NavigationActions'
import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LineFocusIcon from 'LuxKit/Icons/line/LineFocusIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { memo, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { getTourV2OfferPageBaseUrl } from 'selectors/TourV2/tourV2OfferPageSelectors'
import styled from 'styled-components'
import TourV2Itinerary from './TourV2Itinerary'
import TourV2ItineraryPdfDownload from './TourV2ItineraryPdfDownload'
import TourV2RouteMapModal from './TourV2RouteMapModal'

const Root = styled(Group)`
  > .itinerary-area {
    flex-grow: 1;
  }

  > .map-area {
    flex-shrink: 0;
  }
`

const MapColumn = styled(Group)`
  width: 100%;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(457)};
    position: sticky;
    top: ${rem(130)}; /* sticky header height (118) + margin (12) */
  }
`

const MapPreview = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    max-height: ${rem(685)};
  }
`

const RouteMapImageButton = styled(Clickable)`
  display: flex;
  width: 100%;
`

/*
 * Match OfferPageGallery overlay CTAs (CRO-7016): primary + dark.
 * Under cro_rebrand that maps to the white rounded overlay button treatment.
 */
const ExpandMapButton = styled(TextButton)`
  position: absolute;
  inset-inline-end: ${rem(8)};
  bottom: ${rem(8)};
  z-index: 1;
`
interface Props {
  offerType: App.Tours.V2OfferType
  itinerary: Array<App.Tours.TourV2OfferItineraryItem>
  variation?: App.Tours.TourV2OfferVariation
  alternativeVariation?: App.Tours.TourV2OfferVariation
  onAlternativeRouteChange: (variationId: string) => void
  pdfDownload?: ComponentProps<typeof TourV2ItineraryPdfDownload>
}

interface MappedProps {
  baseTourOfferUrl: string
}

function TourV2ItineraryAndMap(props: Props & MappedProps) {
  const {
    itinerary,
    variation,
    offerType,
    alternativeVariation,
    onAlternativeRouteChange,
    baseTourOfferUrl,
    pdfDownload,
  } = props
  const intl = useIntl()
  const routeMapImage = variation?.routeMapImage

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const changeAlternativeVariation = useCallback(() => {
    if (alternativeVariation) {
      offerPageDispatch({ type: OfferPageActions.setTourV2Departure, departure: undefined })
      dispatch(pushWithRegion(
        `${baseTourOfferUrl}/${alternativeVariation.id}`,
        '',
        { scrollBehaviour: 'smooth', scrollTo: '#TOUR_V2_CONTENT_TOP_ANCHOR' },
      ))
      offerPageDispatch({
        type: OfferPageActions.setTourV2Variation,
        variation: alternativeVariation,
      })
      onAlternativeRouteChange(alternativeVariation.id)
    }
  },
  [offerPageDispatch, dispatch, baseTourOfferUrl, onAlternativeRouteChange, alternativeVariation],
  )

  const showModal = useContext(ModalContext)

  const openMapModal = useCallback(() => {
    showModal(<TourV2RouteMapModal variation={variation!} />)
  }, [showModal, variation])

  return <Root direction="vertical-reverse" gap={32} tabletDirection="horizontal">
    <div className="itinerary-area">
      <TourV2Itinerary
        itinerary={itinerary}
        title={intl.formatMessage({
          id: 'ZubnwjKArDObxJWqtJ4-',
          defaultMessage: 'What you’ll see',
          description: 'Heading for tour itinerary section on the tour offer detail page',
        })}
        offerType={offerType}
        alternativeVariation={alternativeVariation}
        changeAlternativeVariation={changeAlternativeVariation}
      />
    </div>

    {(!!routeMapImage || !!pdfDownload) && <div className="map-area">
      <MapColumn direction="vertical" gap={16}>
        {!!routeMapImage && (
          <MapPreview>
            <RouteMapImageButton onClick={openMapModal}>
              <ResponsiveImage
                id={routeMapImage.id!}
                alt={routeMapImage.title}
                tabletWidth="370px"
                desktopWidth="457px"
                mobileAspectRatio="1:1"
                tabletAspectRatio={undefined}
              />
            </RouteMapImageButton>
            <ExpandMapButton
              kind="primary"
              variant="dark"
              size="small"
              startIcon={<LineFocusIcon />}
              onClick={openMapModal}
            >
              {intl.formatMessage({
                id: 'untDSj8ao-A1EU770-oP',
                defaultMessage: 'Expand map',
                description: 'Button text to expand route map in modal on the map view',
              })}
            </ExpandMapButton>
          </MapPreview>
        )}
        {!!pdfDownload && <TourV2ItineraryPdfDownload {...pdfDownload} />}
      </MapColumn>
    </div>}

  </Root>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => ({
  baseTourOfferUrl: getTourV2OfferPageBaseUrl(offerPageState),
})

export default memo(withOfferPageState(mapOfferPageStateToProps, TourV2ItineraryAndMap))
